<template>
  <v-list nav density="compact" class="program-list-group" id="AbstractProgramListGroup">
    <template v-if="isLoggedIn">
      <template v-for="section in visibleSections" :key="section.name">
        <v-list-subheader>{{ section.name }}</v-list-subheader>
        <v-list-item
          v-for="(item, index) in section.items"
          :key="index"
          :to="{ name: item.routeName }"
          :target="item.target"
          link
          class="program-list-item"
          :id="item.linkId"
          :prepend-icon="item.icon"
          :title="item.text"
        ></v-list-item>
      </template>
    </template>

    <v-divider class="my-2"></v-divider>

    <v-list-item
      id="programListLogout"
      class="program-auth-item"
      @click="logout"
      v-if="isLoggedIn"
      :prepend-icon="icons.mdiLogout"
      :title="`Logout (as ${authUser})`"
    ></v-list-item>
  </v-list>
</template>

<script>
import {
  mdiHomeCity,
  mdiMapMarkerRadius,
  mdiFileDocumentEditOutline,
  mdiFileImageOutline,
  mdiImageMultipleOutline,
  mdiMagnify,
  mdiChartBox,
  mdiCog,
  mdiLogout,
  mdiClockOutline,
  mdiAccountSearch,
  mdiHomeSearchOutline,
} from '@mdi/js';
import { mapGetters, mapActions } from 'vuex';

// Matches the backend's authz.js rank table -- kept here too (same
// duplication canEditMixin.js already accepts) so a nav item can be
// hidden by minRole without threading the backend's table through Vuex.
const ROLE_RANK = {
  viewer: 0, editor: 1, deleter: 2, userAdmin: 3, admin: 4,
};

// Sidebar nav pattern styled after tmc-local-frontend's ProgramList.vue
// (v-list nav dense of menu items, each routing to a module) -- but a plain
// flat list within each section, not the auth-role-gated tree that
// ProgramList.vue uses, since this app doesn't have that granular a
// permission model yet.
//
// Grouped into sections (2026-08-25) now that this app covers two separate
// legacy programs sharing one login/database: "Images" (everything from the
// original Humphrey Abstract rebuild) and "Legal" (the "Name and Real Estate
// Index" rebuild -- see Abstract Legal Program's own CLAUDE.md for why these
// share one app instead of being separate programs).
export default {
  name: 'AbstractProgramList',
  data: () => ({
    sections: [
      {
        name: 'Images',
        items: [
          {
            text: 'Additions', routeName: 'Additions', icon: mdiHomeCity, linkId: 'programListAdditions',
          },
          {
            text: 'Sub-Additions',
            routeName: 'SubAdditions',
            icon: mdiMapMarkerRadius,
            linkId: 'programListSubAdditions',
          },
          {
            text: 'Deeds & Instruments',
            routeName: 'Deeds',
            icon: mdiFileDocumentEditOutline,
            linkId: 'programListDeeds',
          },
          {
            text: 'Deed Search',
            routeName: 'DeedSearch',
            icon: mdiMagnify,
            linkId: 'programListDeedSearch',
          },
          {
            text: 'Documents & Images',
            routeName: 'DocsImages',
            icon: mdiFileImageOutline,
            linkId: 'programListDocsImages',
          },
          {
            text: 'Image Viewer',
            routeName: 'ImageViewerPage',
            icon: mdiImageMultipleOutline,
            linkId: 'programListImageViewer',
            // Opens in its own tab (per Michael's request) -- a dedicated,
            // fullscreen viewer is a separate surface from the app's normal
            // single-tab navigation, so it shouldn't replace whatever screen
            // you were already on.
            target: '_blank',
          },
          {
            text: 'Reports', routeName: 'Reports', icon: mdiChartBox, linkId: 'programListReports',
          },
        ],
      },
      {
        name: 'Legal',
        items: [
          {
            text: 'Name Index', routeName: 'NameIndex', icon: mdiAccountSearch, linkId: 'programListNameIndex',
          },
          {
            text: 'Real Estate',
            routeName: 'RealEstateIndex',
            icon: mdiHomeSearchOutline,
            linkId: 'programListRealEstateIndex',
          },
        ],
      },
      {
        name: 'Settings',
        items: [
          {
            text: 'Admin', routeName: 'Admin', icon: mdiCog, linkId: 'programListAdmin',
          },
          {
            text: 'Scheduled Jobs',
            routeName: 'ScheduledJobs',
            icon: mdiClockOutline,
            linkId: 'programListScheduledJobs',
            // Holds FTP credentials for external accounts -- same admin-only
            // gate as the backend's scheduledJobs query/mutations
            // (authz.js). userAdmin can manage Users but not this.
            minRole: 'admin',
          },
        ],
      },
    ],
    icons: {
      mdiLogout,
    },
  }),
  computed: {
    ...mapGetters('auth/token', ['authUser', 'authRole', 'isLoggedIn']),
    visibleSections() {
      return this.sections
        .map((section) => ({
          ...section,
          items: section.items.filter(
            (item) => !item.minRole
              || (ROLE_RANK[this.authRole] ?? -1) >= ROLE_RANK[item.minRole],
          ),
        }))
        .filter((section) => section.items.length > 0);
    },
  },
  methods: {
    ...mapActions('auth/token', { doLogout: 'logout' }),
    logout() {
      this.doLogout();
      this.$router.push({ name: 'Login' });
    },
  },
};
</script>
